import { observer } from 'mobx-react'
import type { CSSProperties, FC, ReactNode } from 'react'
import { useEffect, useMemo, useRef, useState } from 'react'

import { PIXEL_RATIO } from '../constants'
import ScalableContent from '../scalableContent/ScalableContent'
import type { FitMode } from '../scalableContent/scalableContent.types'
import type { Size } from '../types'
import type { NoteData } from './handwrittenNote.types'
import HandwrittenNotePath from './HandwrittenNotePath'
import { MIN_TEXT_BOX_HEIGHT, MIN_TEXT_BOX_WIDTH } from './HandwrittenNoteTextBox'
import HandwrittenNoteService from './service/HandwrittenNote.service'
import HandwrittenNotePathItem from './service/HandwrittenNotePathItem'

const kind = `mn__ReadOnlyHandwrittenNote`

interface Props {
  /**
   * Writable 이었던 note data
   */
  noteData?: NoteData
  /**
   * Readonly 만 가능했던 note data
   */
  readonlyNoteData?: NoteData
  renderPriority?: 'readonlyNoteData' | 'noteData'
  contentSize?: Size // noteData가 없으면 contentSize 필수
  preventScale?: boolean // panning, pinch to zoom 방지
  isHidden?: boolean // 필기 숨기기
  fitMode?: FitMode
  containerPadding?: number
  zoomLevel?: number
  noteScaleResetKey?: number
  isSlideToNoteRect?: boolean
  preventResizeObserver?: boolean
  preventOverflow?: boolean
  style?: CSSProperties
  contentWrapperStyle?: CSSProperties
  contentStyle?: CSSProperties
  children?: ReactNode
}

const ReadOnlyHandwrittenNote: FC<Props> = ({
  noteData,
  readonlyNoteData,
  renderPriority = 'noteData',
  contentSize: initialContentSize,
  preventScale,
  isHidden,
  fitMode = 'exact',
  containerPadding = 0,
  zoomLevel = 1,
  noteScaleResetKey,
  isSlideToNoteRect,
  preventResizeObserver,
  preventOverflow,
  style,
  contentWrapperStyle,
  contentStyle,
  children,
}) => {
  const serviceRef = useRef(new HandwrittenNoteService())
  const service = serviceRef.current
  const containerRef = useRef<HTMLDivElement>(null)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    setIsReady(true)
  }, [])

  const contentSize = useMemo(() => {
    if (!noteData && !readonlyNoteData) {
      return initialContentSize
    }
    return {
      width: Math.max(noteData?.viewBox[0] ?? 0, readonlyNoteData?.viewBox[0] ?? 0),
      height: Math.max(noteData?.viewBox[1] ?? 0, readonlyNoteData?.viewBox[1] ?? 0),
    }
  }, [noteData, initialContentSize])

  useEffect(() => {
    if (!noteData) {
      return
    }
    service.reset()
    service.noteData.fromJSON(noteData)
    service.setViewBoxSize(contentSize)
  }, [contentSize, noteData, service])

  const targetRect = useMemo(() => {
    if (!noteData || !isSlideToNoteRect) {
      return
    }
    const paths = noteData.paths
    const targetItems: Array<{
      indexes: number[]
      area: number
    }> = [
      {
        indexes: [],
        area: 0,
      },
    ]
    paths.forEach((path, index, list) => {
      if (path.pathRect) {
        let delay: number | undefined = undefined
        if (path.createdAt && list[index - 1]?.updatedAt) {
          delay = path.createdAt - list[index - 1].updatedAt!

          if (delay > 1000) {
            targetItems.push({
              indexes: [],
              area: 0,
            })
          }
        }
        targetItems[targetItems.length - 1].indexes.push(index)
        targetItems[targetItems.length - 1].area += path.pathRect.width * path.pathRect.height
      }
    })
    const maxAreaPaths = targetItems
      .filter((item) => item.indexes.length)
      .sort((a, b) => b.area - a.area)[0]

    if (!maxAreaPaths) {
      return
    }

    let minX = Number.MAX_SAFE_INTEGER
    let maxX = Number.MIN_SAFE_INTEGER
    let minY = Number.MAX_SAFE_INTEGER
    let maxY = Number.MIN_SAFE_INTEGER

    for (const index of maxAreaPaths.indexes) {
      const rect = paths[index]?.pathRect
      if (!rect) {
        continue
      }
      minX = Math.min(minX, rect.x)
      maxX = Math.max(maxX, rect.x + rect.width)
      minY = Math.min(minY, rect.y)
      maxY = Math.max(maxY, rect.y + rect.height)
    }
    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
    }
  }, [isSlideToNoteRect, noteData])

  if (!contentSize && !noteData && !readonlyNoteData) {
    return null
  }

  const viewBoxSizeString = contentSize
    ? `0 0 ${contentSize.width * PIXEL_RATIO} ${contentSize.height * PIXEL_RATIO}`
    : undefined

  const needPreventOverflow = isHidden || preventScale || preventOverflow
  const allowDefaultTouchAction = preventScale && preventOverflow

  const readonlyPathItems = useMemo(() => {
    if (!readonlyNoteData) {
      return []
    }
    return readonlyNoteData.paths.map(HandwrittenNotePathItem.fromData)
  }, [readonlyNoteData])

  return (
    <div
      ref={containerRef}
      data-component={kind}
      style={{
        overflow: needPreventOverflow ? 'hidden' : 'auto',
        flexGrow: 1,
        display: 'flex',
        height: '100%',
        position: 'relative',
        ...style,
      }}
    >
      {isReady && containerRef.current && (
        <div
          style={{
            display: 'flex',
            flexGrow: 1,
            ...contentWrapperStyle,
          }}
        >
          <ScalableContent
            containerEl={containerRef.current}
            contentSize={contentSize}
            isWritingMode={false}
            preventScale={preventScale}
            fitMode={fitMode}
            containerPadding={containerPadding}
            zoomLevel={zoomLevel}
            targetRect={targetRect}
            allowDefaultTouchAction={allowDefaultTouchAction}
            preventResizeObserver={preventResizeObserver}
            noteScaleResetKey={noteScaleResetKey}
          >
            <div
              style={{
                ...contentStyle,
              }}
            >
              {children}
              {readonlyNoteData && (
                <>
                  <svg
                    style={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                      display: isHidden ? 'none' : 'unset',
                      zIndex: renderPriority === 'readonlyNoteData' ? 1 : 0,
                    }}
                    viewBox={viewBoxSizeString}
                  >
                    {readonlyPathItems.map((item) => (
                      <HandwrittenNotePath key={item.id} item={item} service={service} />
                    ))}
                  </svg>
                  <div
                    style={{
                      position: 'absolute',
                      inset: 0,
                      width: '100%',
                      height: '100%',
                      display: isHidden ? 'none' : 'unset',
                    }}
                  >
                    {readonlyNoteData.textItems?.map((item) => {
                      const scaleX = contentSize
                        ? contentSize.width / readonlyNoteData.viewBox[0]
                        : 1
                      const scaleY = contentSize
                        ? contentSize.height / readonlyNoteData.viewBox[1]
                        : 1
                      const width = (item.width || MIN_TEXT_BOX_WIDTH) * scaleX
                      const height = (item.height || MIN_TEXT_BOX_HEIGHT) * scaleY
                      return (
                        <div
                          key={item.id}
                          style={{
                            position: 'absolute',
                            left: item.x * scaleX,
                            top: item.y * scaleY,
                            width: width,
                            height: height,
                            minWidth: MIN_TEXT_BOX_WIDTH * scaleX,
                            minHeight: MIN_TEXT_BOX_HEIGHT * scaleY,
                            color: item.color,
                            fontSize: item.fontSize * Math.min(scaleX, scaleY),
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                            padding: '18px 24px',
                            boxSizing: 'border-box',
                            overflow: 'visible',
                            display: 'flex',
                            alignItems: 'flex-start',
                            lineHeight: 1.2,
                          }}
                        >
                          {item.text}
                        </div>
                      )
                    })}
                  </div>
                </>
              )}
              {noteData && (
                <svg
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    touchAction: allowDefaultTouchAction ? 'auto' : 'none',
                    display: isHidden ? 'none' : 'unset',
                    width: `${PIXEL_RATIO * 100}%`,
                    height: `${PIXEL_RATIO * 100}%`,
                    transform: `scale(${1 / PIXEL_RATIO})`,
                    transformOrigin: '0 0',
                  }}
                  viewBox={viewBoxSizeString}
                >
                  {service.noteData.visiblePathItems.map((item) => (
                    <HandwrittenNotePath key={item.id} item={item} service={service} />
                  ))}
                </svg>
              )}
            </div>
          </ScalableContent>
        </div>
      )}
    </div>
  )
}

export default observer(ReadOnlyHandwrittenNote)
